import { useForm, usePage } from '@inertiajs/react'
import { Mail, Shield, Calendar, Loader2 } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { DashboardPageProps } from '@/types'

type Profile = {
  id: string
  name: string
  email_address: string
  role: 'member' | 'admin' | 'owner'
  created_at: string
}

type ProfileEditProps = {
  profile: Profile
}

function getRoleBadge(role: string) {
  switch (role) {
    case 'owner':
      return <Badge variant="default">Owner</Badge>
    case 'admin':
      return <Badge variant="secondary">Admin</Badge>
    default:
      return <Badge variant="outline">Member</Badge>
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function ProfileEdit({ profile }: ProfileEditProps) {
  const { auth } = usePage<DashboardPageProps>().props
  const accountSlug = auth.account?.slug

  const { data, setData, patch, processing, errors, isDirty } = useForm({
    name: profile.name,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/${accountSlug}/profile`)
  }

  return (
    <DashboardLayout title="Profile">
      <div className="space-y-8">
        <div>
          <h1 className="text-xl font-semibold">Profile</h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            Manage your profile settings for this space
          </p>
        </div>

        {/* Profile information */}
        <div className="pb-8 border-b border-border/40">
          <div className="mb-4">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Profile information</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Update your display name and view your account details
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Display Name</Label>
              <Input
                id="name"
                type="text"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
                className="h-8 text-sm"
                placeholder="Your name"
                required
              />
              {errors.name && (
                <p className="text-[13px] text-destructive">{errors.name}</p>
              )}
              <p className="text-[12px] text-muted-foreground">
                This is how your name appears to other people in this space.
              </p>
            </div>

            <div className="flex justify-end">
              <Button type="submit" size="sm" className="h-8 text-[13px]" disabled={processing || !isDirty}>
                {processing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Saving...
                  </span>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Account details */}
        <div>
          <div className="mb-4">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Account details</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Information about you in this space
            </p>
          </div>
          <div className="space-y-0">
            <div className="flex items-center justify-between py-3 border-b border-border/40">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Email Address</p>
                  <p className="text-[13px] mt-0.5">{profile.email_address}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-border/40">
              <div className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Role</p>
                  <p className="text-[13px] mt-0.5 text-muted-foreground">Your role in this space</p>
                </div>
              </div>
              {getRoleBadge(profile.role)}
            </div>

            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Member Since</p>
                  <p className="text-[13px] mt-0.5">{formatDate(profile.created_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
